/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';

import { FilePenLine } from 'lucide-react';

import { request } from '@mairistem/tools';
import { App, Feature, Menu, render, SpeedDial } from '@mairistem/module';
import { Segment, Icon, Image, Tooltip } from '@mairistem/composants';

import { Context } from '@/components/Context';
import { ModalToastContainer } from '@/components/Common';
import { Dashboard } from '@/components/Dashboard';
import { TypeListePacs } from '@/enums';
import PacsListeContainer from '@/components/Pacs/Liste/PacsListeContainer';
import PacsFicheContainer from '@/components/Pacs/Fiche/PacsFicheContainer';
import AssistantContextProvider from '@/contexts/AssistantContextProvider';
import ListeHubee from '@/components/Hubee/Liste/ListeHubee';
import DemandeHubee from '@/components/Hubee/DetailDemande/DemandeHubee';
import { ParametresHubee, ParametresInsee } from '@/components/Parametres';
import { PageHistoriqueInsee, PageTransfertInsee } from '@/components/Insee';

import { useEntiteStore } from './zustand';

import { initialize } from './sentry';

import './rsuite-layered.css';
import './index.less';
import 'react-toastify/dist/ReactToastify.css';
import { Assistant } from './components/Assistant';
import hubee from './assets/images/hubee.png';
import insee from './assets/images/insee.png';

initialize();

request.interceptors.request.use((config) => {
  if (process.env.NODE_ENV === 'production') {
    if (_.includes(window.location.href, '.jvsonline.fr')) {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.jvsonline.fr';
    } else if (_.includes(window.location.href, '.qa.')) {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.qa.jvs-mairistem.dev';
    } else if (_.includes(window.location.href, '.pp.')) {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.pp.jvs-mairistem.dev';
    } else {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.jvs-mairistem.dev';
    }
  } else {
    config.headers['X-Database'] = process.env.X_DATABASE;
    config.headers['X-Login'] = 2; // TEMPORAIRE
  }

  return config;
});

const EntiteHandler = () => {
  const setRefresh = useEntiteStore((state) => state.setRefresh);

  React.useEffect(() => {
    setRefresh(true);
  }, [setRefresh]);

  return <div />;
};

const code = '360_PACS';
const showTooltips = !window.location.href.includes('360');

// Ne pas modifier ou supprimer
// Utile en mode production afin que le style se place au bonne endroit
if (process.env.NODE_ENV !== 'development') {
  const oldCreatePortal = ReactDOM.createPortal;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ReactDOM.createPortal = (child, domNode) =>
    oldCreatePortal(
      child,
      domNode === document.body
        ? document.querySelector(`#${CSS.escape(code)}`) || domNode
        : domNode || document.querySelector(`#${CSS.escape(code)}`),
    );
}

render(
  <App
    group="administres"
    name="PACS"
    code={code}
    request={request}
    context={Context}
    additionalProductCodesUpdate={['HOL_ETCV']}
  >
    <Menu
      name="Entités"
      dataTestId="menu-entites"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Icon name="building" />}
            message="Entités"
            position="right center"
          />
        ) : (
          <Icon name="building" />
        )
      }
      path="/entites"
    >
      <Feature>
        <Feature.Content>
          <>
            <ModalToastContainer />
            <EntiteHandler />
          </>
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Accueil"
      dataTestId="menu-accueil"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Icon name="home" />}
            message="Accueil"
            position="right center"
          />
        ) : (
          <Icon name="home" />
        )
      }
      path="/"
      exact
    >
      <Feature>
        <Feature.Content>
          <>
            <ModalToastContainer />
            <Segment
              basic
              secondary
              style={{ margin: 0, height: '100%', padding: 0 }}
              className="dashboard-segment"
            >
              <Dashboard />
            </Segment>
          </>
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Paramètres"
      dataTestId="menu-parametres"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Icon name="settings" />}
            message="Paramètres"
            position="right center"
          />
        ) : (
          <Icon name="settings" />
        )
      }
      path="/parametres"
    >
      <Menu name="HubEE" path="/hubee" dataTestId="sous-menu-parametres-hubee">
        <Feature>
          <Feature.Content>
            <>
              <ModalToastContainer />
              <ParametresHubee />
            </>
          </Feature.Content>
        </Feature>
      </Menu>
      <Menu name="INSEE" path="/insee" dataTestId="sous-menu-parametres-insee">
        <Feature>
          <Feature.Content>
            <>
              <ModalToastContainer />
              <ParametresInsee />
            </>
          </Feature.Content>
        </Feature>
      </Menu>
    </Menu>
    <Menu.Separator />
    <Menu
      name="Pacte civil de solidarité"
      dataTestId="menu-pacs"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<FilePenLine />}
            message="Paramètres"
            position="right center"
          />
        ) : (
          <FilePenLine />
        )
      }
      path="/pacs"
      pop
    >
      <Menu name="Projet" path="/projets" dataTestId="sous-menu-pacs-projets">
        <Feature>
          <Feature.Content>
            <Segment basic secondary style={{ margin: 0, height: '100%' }}>
              <Segment
                raised
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: '16px',
                }}
              >
                <ModalToastContainer />
                <PacsListeContainer
                  data-testid="pacs-liste-projets"
                  projet
                  typeListe={`
                    ${TypeListePacs.PacsProjet},
                  `}
                />
              </Segment>
            </Segment>
          </Feature.Content>
        </Feature>
        <Feature path="/:identifiant(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})">
          <Feature.Content>
            <PacsFicheContainer />
          </Feature.Content>
        </Feature>
      </Menu>
      <Menu name="PACS" path="/pacs" dataTestId="sous-menu-pacs-pacs">
        <Feature>
          <Feature.Content>
            <Segment basic secondary style={{ margin: 0, height: '100%' }}>
              <Segment
                raised
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: '16px',
                }}
              >
                <ModalToastContainer />
                <PacsListeContainer
                  data-testid="pacs-liste-pacs"
                  typeListe={`
                    ${TypeListePacs.PacsEnCours},
                    ${TypeListePacs.PacsDissous},
                    ${TypeListePacs.PacsIrrecevable}
                  `}
                />
              </Segment>
            </Segment>
          </Feature.Content>
        </Feature>
        <Feature path="/all">
          <Feature.Content>
            <Segment basic secondary style={{ margin: 0, height: '100%' }}>
              <Segment
                raised
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: '16px',
                }}
              >
                <ModalToastContainer />
                <PacsListeContainer
                  data-testid="pacs-liste-tout"
                  typeListe={TypeListePacs.ToutPacs}
                />
              </Segment>
            </Segment>
          </Feature.Content>
        </Feature>
        <Feature path="/:identifiant(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})">
          <Feature.Content>
            <PacsFicheContainer />
          </Feature.Content>
        </Feature>
      </Menu>
    </Menu>

    <Menu
      name="Liste des demandes HubEE"
      dataTestId="menu-hubee"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={
              <Image
                src={hubee}
                style={{ borderRadius: '20px', height: 20, width: 20 }}
              />
            }
            message="Liste des demandes HubEE"
            position="right center"
          />
        ) : (
          <Image
            src={hubee}
            style={{ borderRadius: '20px', height: 20, width: 20 }}
          />
        )
      }
      path="/hubee"
    >
      <Feature>
        <Feature.Content>
          <Segment basic secondary style={{ margin: 0, height: '100%' }}>
            <Segment
              raised
              style={{
                height: '100%',
                overflow: 'hidden',
                borderRadius: '16px',
              }}
            >
              <ModalToastContainer />
              <ListeHubee />
            </Segment>
          </Segment>
        </Feature.Content>
      </Feature>
      <Feature path="/:identifiant">
        <Feature.Content>
          <>
            <ModalToastContainer />
            <DemandeHubee />
          </>
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Transmission INSEE"
      dataTestId="menu-insee"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Image src={insee} style={{ height: 20, width: 20 }} />}
            message="Transmission INSEE"
            position="right center"
          />
        ) : (
          <Image src={insee} style={{ height: 20, width: 20 }} />
        )
      }
      path="/insee"
      pop
    >
      <Menu
        name="Transfert"
        path="/transfert"
        dataTestId="sous-menu-insee-transfert"
      >
        <Feature>
          <Feature.Content>
            <>
              <ModalToastContainer />
              <PageTransfertInsee />
            </>
          </Feature.Content>
        </Feature>
      </Menu>
      <Menu
        name="Historique"
        path="/historique"
        dataTestId="sous-menu-insee-historique"
      >
        <Feature>
          <Feature.Content>
            <>
              <ModalToastContainer />
              <PageHistoriqueInsee />
            </>
          </Feature.Content>
        </Feature>
      </Menu>
    </Menu>
    <SpeedDial>
      <SpeedDial.Button
        name="pacs"
        title="Créer un PACS"
        icon="file"
        path="/pacs/assistant"
      />
    </SpeedDial>
    <Feature path="/assistant">
      <Feature.Content>
        <>
          <ModalToastContainer />
          <AssistantContextProvider>
            <Assistant />
          </AssistantContextProvider>
        </>
      </Feature.Content>
    </Feature>
  </App>,
);
